import type { Question } from '../../types';

// Vite resolves these as bundled strings — keep authoring in CLAUDE.md / types.ts.
import bespokeGuideMd from '../CLAUDE.md?raw';
import layoutTypesTs from '../layout/types.ts?raw';

function liteQuestionSnapshot(q: Question): Record<string, unknown> {
  const snap: Record<string, unknown> = {
    qid: q.qid,
    question: q.question,
    type: q.type,
  };
  if (q.description) snap.description = q.description;
  if (q.values) snap.values = q.values;
  if (q.is_pseudo) snap.is_pseudo = true;
  if (q.value_description) snap.value_description = q.value_description;
  if (q.subitems) {
    const out: Record<string, unknown[]> = {};
    for (const [parentKey, sub] of Object.entries(q.subitems)) {
      out[parentKey] = sub.map(liteQuestionSnapshot);
    }
    snap.subitems = out;
  }
  return snap;
}

/**
 * Offline LLM paste bundle — **section schema metadata + bespoke DSL**.
 * Intended for admins only; excludes patient PHI and broader app internals.
 */
export function composeVibecodeLlmBundle(input: {
  visitTypeSummary: string;
  schemaVersionName: string;
  sectionName: string;
  /** Section question tree aligned with RogerDocs schema_data */
  questions: Question[];
}): string {
  const sectionJson = JSON.stringify(
    input.questions.map(liteQuestionSnapshot),
    null,
    2
  );

  return [
    '# Bespoke JSON layout authoring context (Roger EMR)',
    '',
    '_Only use QIDs listed under “Section schema”. Tailwind/CSS class strings in JSON must compile (project safelist gaps can drop dynamic utilities)._',
    '',
    `## Routing metadata`,
    `- Visit type selector (API): ${input.visitTypeSummary}`,
    `- schema_version.name: "${input.schemaVersionName}"`,
    `- section_name key: "${input.sectionName}"`,
    '',
    '## Section schema (AUTHORITATIVE QIDs AND TYPES)',
    '`layout_json` must render every Question below as an answerable field (reachable under some answers). Pseudos included.',
    '```json',
    sectionJson,
    '```',
    '',
    '## Narrative authoring guide (`bespoke/CLAUDE.md`)',
    bespokeGuideMd.trimEnd(),
    '',
    '## Layout node TypeScript unions (`layout/types.ts`) — ground-truth DSL',
    '`import type { LayoutNode } from "./layout/types"` is the authoritative node graph.',
    '```typescript',
    layoutTypesTs.trimEnd(),
    '```',
    '',
    '## Constraints',
    '- Output must be JSON: a top-level **`LayoutNode[]`** array.',
    '- Use only **`layout/types`** `type` discriminators.',
    '- Use **`schemaMap`** lookups for labels — every control needs a **`qid` present above**.',
    '- No patient data, no PHI, no external URLs — schema text only.',
  ].join('\n');
}
